import type { QuestionsListForChart } from "@models/Dataset";
import type { QuestionResponsesSearchFilter } from "@models/Filter";
import type { WidgetRollup } from "@models/Rollup";
import type {
  ScorecardBrandMapping,
  ScorecardQuestionRow,
  ScorecardQuestionRowDefinition,
} from "@models/Scorecard";
import { normalizeLabel } from "@utils/scorecard-utils";

export interface ScorecardCellValue {
  value: number | null;
  baseN: number | null;
  suppressed: boolean;
}

export type ChartData = QuestionsListForChart["data"];

export type ScorecardRowFetchShape = "flat" | "crosstab_diagonal";

export interface ScorecardRowFetchPlan {
  fetchShape: ScorecardRowFetchShape;
  questionId: string;
  filters: QuestionResponsesSearchFilter[];
  focusParams: { focusMode?: string; focusEntity?: string; focusRollup?: WidgetRollup };
  crossQuestionId?: string;
  crossFocusParams?: {
    crossFocusMode?: string;
    crossFocusEntity?: string;
    crossFocusRollup?: WidgetRollup;
  };
}

/** Focus params for a Single grid row's own value/rollup selection — narrows
 *  the VALUE axis to one derived score per row-variable, keeping variables
 *  (brands) as the axis. One_dimensional rows never need focus params, since
 *  every brand's own response category IS the metric already. */
export function getFocusParamsForRow(row: ScorecardQuestionRow): {
  focusMode?: string;
  focusEntity?: string;
  focusRollup?: WidgetRollup;
} {
  if (row.sourceQuestionKind !== "single_grid") return {};
  if (row.rollup) return { focusMode: "VALUE", focusRollup: row.rollup };
  if (row.metricValueCode) return { focusMode: "VALUE", focusEntity: row.metricValueCode };
  return {};
}

/** Same as getFocusParamsForRow but cross-prefixed — narrows the Base
 *  question's VALUE axis (e.g. a 1-5 familiarity scale) to one derived score
 *  per cross-variable, keeping the Base question's own brand/variable axis
 *  intact as the crosstab's cross-columns. Works for either Base source:
 *  an Existing Metric Row's own definition, or a New Base Metric's. */
export function getCrossFocusParamsForBaseRow(base: ScorecardQuestionRowDefinition): {
  crossFocusMode?: string;
  crossFocusEntity?: string;
  crossFocusRollup?: WidgetRollup;
} {
  if (base.sourceQuestionKind !== "single_grid") return {};
  if (base.rollup) return { crossFocusMode: "VALUE", crossFocusRollup: base.rollup };
  if (base.metricValueCode) {
    return { crossFocusMode: "VALUE", crossFocusEntity: base.metricValueCode };
  }
  return {};
}

/** Resolves which brand mapping a row's Scorecard Metric Base uses to
 *  qualify a given brand's respondents — the referenced row's own mapping
 *  for Existing Metric Row, or the New Base Metric's own independently
 *  resolved mapping. */
export function getBaseMappingForBrand(
  row: ScorecardQuestionRow,
  brandId: string,
  allRows: ScorecardQuestionRow[],
): ScorecardBrandMapping | undefined {
  const base = row.rowBase;
  if (base?.kind === "existing_question_row") {
    const baseRow = allRows.find((r) => r.id === base.questionRowId);
    return baseRow?.brandMappings.find((m) => m.brandId === brandId);
  }
  if (base?.kind === "new_question_base") {
    return base.questionBase.brandMappings.find((m) => m.brandId === brandId);
  }
  return undefined;
}

/** Decides how a row should be fetched based on its Scorecard Metric Base:
 *  both Existing Metric Row and New Base Metric produce a crosstab-diagonal
 *  request (crossQuestionId + cross-focus params) — the only difference is
 *  where that cross question comes from (another row vs. an independent
 *  definition embedded on this row). No Base falls back to the flat request. */
export function buildRowFetchPlan(
  row: ScorecardQuestionRow,
  allRows: ScorecardQuestionRow[],
  baseFilters: QuestionResponsesSearchFilter[],
): ScorecardRowFetchPlan {
  const focusParams = getFocusParamsForRow(row);
  const base = row.rowBase;

  if (base?.kind === "existing_question_row") {
    const baseRow = allRows.find((r) => r.id === base.questionRowId);
    if (baseRow) {
      return {
        fetchShape: "crosstab_diagonal",
        questionId: row.sourceQuestionId,
        filters: baseFilters,
        focusParams,
        crossQuestionId: baseRow.sourceQuestionId,
        crossFocusParams: getCrossFocusParamsForBaseRow(baseRow),
      };
    }
  }

  if (base?.kind === "new_question_base") {
    return {
      fetchShape: "crosstab_diagonal",
      questionId: row.sourceQuestionId,
      filters: baseFilters,
      focusParams,
      crossQuestionId: base.questionBase.sourceQuestionId,
      crossFocusParams: getCrossFocusParamsForBaseRow(base.questionBase),
    };
  }

  return { fetchShape: "flat", questionId: row.sourceQuestionId, filters: baseFilters, focusParams };
}

/** The question-detail endpoint (rowtitle/title) and the response/data
 *  endpoint (variableLabel) don't always agree on which label field they
 *  echo back for the same variable — match against every label variant a
 *  mapping recorded, not just the one chosen for display. */
function mappingMatchesLabel(mapping: ScorecardBrandMapping, responseLabel: string): boolean {
  const candidates =
    mapping.matchLabels?.length ? mapping.matchLabels : [mapping.boundResponseLabel];
  const normalizedResponse = normalizeLabel(responseLabel);
  return candidates.some((c) => !!c && normalizeLabel(c) === normalizedResponse);
}

/** Flat (no Base) cell lookup. */
export function extractCellFromData(
  data: ChartData,
  mapping: ScorecardBrandMapping,
): ScorecardCellValue | null {
  const entry = data.find((item) => mappingMatchesLabel(mapping, item.variableLabel ?? ""));
  if (!entry) return null;
  const survey = entry.surveys?.[0];
  if (!survey) return null;

  const metric = survey.descriptiveMetrics?.[0];
  return {
    value: metric?.value ?? survey.percentage ?? null,
    baseN: metric?.baseN ?? null,
    suppressed: metric?.suppressed ?? false,
  };
}

/** Diagonal (Existing Metric Row Base) cell lookup: within the row-entry
 *  matching this brand's own response category (row axis, same as flat),
 *  find the descriptiveMetrics entry whose crossVariableLabel matches this
 *  SAME brand's response category on the Base question (cross axis) — each
 *  brand qualifies against its own Base value, not a shared column. */
export function extractDiagonalCellFromData(
  data: ChartData,
  rowMapping: ScorecardBrandMapping,
  baseMapping: ScorecardBrandMapping | undefined,
): ScorecardCellValue | null {
  if (!baseMapping?.boundResponseLabel) return null;
  const entry = data.find((item) => mappingMatchesLabel(rowMapping, item.variableLabel ?? ""));
  if (!entry) return null;
  const survey = entry.surveys?.[0];
  if (!survey) return null;
  const metric = survey.descriptiveMetrics?.find((m) =>
    mappingMatchesLabel(baseMapping, m.crossVariableLabel ?? ""),
  );
  if (!metric) return null;
  return {
    value: metric.value ?? null,
    baseN: metric.baseN ?? null,
    suppressed: metric.suppressed ?? false,
  };
}

/** Order-independent signature for a resolved filter set — used as part of
 *  the per-question fetch cache key so two rows sharing a source question
 *  but carrying different Bases don't collide. */
export function getBaseFilterSignature(filters: QuestionResponsesSearchFilter[]): string {
  if (filters.length === 0) return "";
  const entries = filters.map((f) =>
    JSON.stringify({
      ...f,
      selectedValues: [...f.selectedValues].sort(),
      values: [...f.values].sort(),
      variables: [...f.variables].sort(),
    }),
  );
  return entries.sort().join("|");
}
